import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "@tanstack/react-form";
import { ApiInternal } from "src/api/api_internal";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import Form from "src/components/base/form";
import { Field, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Textarea } from "src/components/base/textarea";
import { Button, PrimaryButton } from "src/components/base/button";
import {
    GetPendingRoutesResponseItem,
    GetPendingRoutesResponseItemSubnetOneOf,
} from "src/api/generated/frontend_internal_v1";

/**
 * The properties for {@link CreateSubnetDialog}
 */
export type CreateSubnetDialogProps = {
    /** The subnet to create and its first router */
    route: (GetPendingRoutesResponseItem & { subnet: GetPendingRoutesResponseItemSubnetOneOf }) | undefined;
    /** Callback when the form is submitted */
    onSubmit: () => void;
    /** Callback when the dialog is closed */
    onClose: () => void;
};

/**
 * A dialog to create a new subnet with its first router
 */
export default function CreateSubnetDialog(props: CreateSubnetDialogProps) {
    const [t] = useTranslation("dialog-approve-subnet");
    const [tg] = useTranslation();

    const form = useForm({
        defaultValues: {
            description: "",
        },
        onSubmit: async ({ value }) => {
            if (!props.route) return;
            await ApiInternal.subnets.createSubnetWithFstRouter(
                props.route.subnet.name,
                props.route.device.uuid,
                value.description,
            );

            props.onSubmit();
        },
    });

    useEffect(() => {
        form.reset();
    }, [props.route]);

    if (!props.route) return null;

    return (
        <Dialog open onClose={props.onClose}>
            <DialogTitle>{t("heading.approve-subnet")}</DialogTitle>
            <DialogBody>
                <Form onSubmit={form.handleSubmit}>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field name={"description"}>
                                {(fieldApi) => (
                                    <Field>
                                        <Label>{t("label.description")}</Label>
                                        <Textarea
                                            rows={4}
                                            maxLength={1024}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                            value={fieldApi.state.value}
                                        />
                                    </Field>
                                )}
                            </form.Field>
                        </FieldGroup>
                    </Fieldset>

                    <DialogActions>
                        <Button plain={true} onClick={props.onClose}>
                            {tg("button.cancel")}
                        </Button>
                        <PrimaryButton type={"submit"}>{t("button.approve")}</PrimaryButton>
                    </DialogActions>
                </Form>
            </DialogBody>
        </Dialog>
    );
}
